import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Box,
  ExternalLink,
  Pencil,
  Plus,
  Trash2,
} from 'lucide-react';
import { useState, type FormEvent } from 'react';

import { ApiError, apiFetch } from '@/lib/api';

type ClientServiceStatus = 'ACTIVE' | 'TESTING' | 'SUSPENDED' | 'CANCELLED';

interface ClientServiceItem {
  id: string;
  clientId: string;
  name: string;
  category: string | null;
  status: ClientServiceStatus;
  version: string | null;
  url: string | null;
  startedAt: string | null;
  lastUpdatedAt: string | null;
  nextUpdateAt: string | null;
  notes: string | null;
}

const STATUS_LABEL: Record<ClientServiceStatus, string> = {
  ACTIVE: 'Ativo',
  TESTING: 'Em teste',
  SUSPENDED: 'Suspenso',
  CANCELLED: 'Cancelado',
};

const STATUS_STYLE: Record<ClientServiceStatus, string> = {
  ACTIVE: 'bg-emerald-500/10 text-emerald-600',
  TESTING: 'bg-blue-500/10 text-blue-600',
  SUSPENDED: 'bg-amber-500/10 text-amber-600',
  CANCELLED: 'bg-destructive/10 text-destructive',
};

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('pt-BR');
  } catch {
    return iso;
  }
}

function daysSince(iso: string | null): number | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
}

export function ClientServicesTab({ clientId }: { clientId: string }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<ClientServiceItem | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const list = useQuery({
    queryKey: ['client-services', clientId],
    queryFn: () => apiFetch<ClientServiceItem[]>(`/clients/${clientId}/services`),
  });

  const remove = useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/clients/${clientId}/services/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['client-services', clientId] }),
  });

  const items = list.data ?? [];
  const active = items.filter((s) => s.status === 'ACTIVE').length;
  const testing = items.filter((s) => s.status === 'TESTING').length;
  const overdueUpdate = items.filter((s) => {
    if (s.status !== 'ACTIVE') return false;
    if (!s.nextUpdateAt) return false;
    return new Date(s.nextUpdateAt) < new Date();
  }).length;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          Sistemas, integrações e produtos que este cliente está usando hoje.
        </div>
        <button
          type="button"
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-1 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" /> Novo serviço
        </button>
      </div>

      {items.length > 0 && (
        <div className="grid grid-cols-3 gap-3">
          <Stat label="Ativos" value={active} tone="good" />
          <Stat label="Em teste" value={testing} tone="info" />
          <Stat
            label="Atualização atrasada"
            value={overdueUpdate}
            tone={overdueUpdate > 0 ? 'warn' : 'neutral'}
          />
        </div>
      )}

      {list.isLoading && <p className="text-sm text-muted-foreground">Carregando…</p>}
      {items.length === 0 && !list.isLoading && (
        <div className="flex flex-col items-center gap-3 rounded-md border border-dashed border-border p-12 text-center">
          <Box className="h-10 w-10 text-muted-foreground/50" />
          <p className="text-sm text-muted-foreground">
            Nenhum serviço cadastrado. Adicione os sistemas/integrações ativos deste cliente.
          </p>
        </div>
      )}
      {items.length > 0 && (
        <div className="space-y-2">
          {items.map((s) => (
            <ServiceCard
              key={s.id}
              item={s}
              onEdit={() => setEditing(s)}
              onDelete={() => {
                if (confirm(`Excluir serviço "${s.name}"?`)) remove.mutate(s.id);
              }}
            />
          ))}
        </div>
      )}

      {showCreate && (
        <ServiceFormDialog
          clientId={clientId}
          onClose={() => setShowCreate(false)}
          onSaved={() => {
            setShowCreate(false);
            qc.invalidateQueries({ queryKey: ['client-services', clientId] });
          }}
        />
      )}
      {editing && (
        <ServiceFormDialog
          clientId={clientId}
          item={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            qc.invalidateQueries({ queryKey: ['client-services', clientId] });
          }}
        />
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'good' | 'info' | 'warn' | 'neutral';
}) {
  const toneClass =
    tone === 'good'
      ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
      : tone === 'info'
        ? 'border-blue-300 bg-blue-50 text-blue-800'
        : tone === 'warn'
          ? 'border-amber-300 bg-amber-50 text-amber-800'
          : 'border-border bg-card text-muted-foreground';
  return (
    <div className={`rounded-md border p-3 ${toneClass}`}>
      <div className="text-2xl font-semibold">{value}</div>
      <div className="text-xs">{label}</div>
    </div>
  );
}

function ServiceCard({
  item,
  onEdit,
  onDelete,
}: {
  item: ClientServiceItem;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const updateOverdue =
    item.status === 'ACTIVE' &&
    item.nextUpdateAt &&
    new Date(item.nextUpdateAt) < new Date();
  const daysFromLast = daysSince(item.lastUpdatedAt);

  return (
    <div
      className={`rounded-md border bg-card p-3 ${updateOverdue ? 'border-amber-400' : 'border-border'}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium">{item.name}</span>
            <span
              className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs ${STATUS_STYLE[item.status]}`}
            >
              {STATUS_LABEL[item.status]}
            </span>
            {item.category && (
              <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                {item.category}
              </span>
            )}
            {item.version && (
              <span className="text-xs font-mono text-muted-foreground">
                {item.version}
              </span>
            )}
            {updateOverdue && (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 px-2 py-0.5 text-xs font-medium text-amber-700">
                <AlertTriangle className="h-3 w-3" /> Atualização atrasada
              </span>
            )}
          </div>
          {item.url && (
            <a
              href={item.url}
              target="_blank"
              rel="noreferrer"
              className="mt-1 inline-flex items-center gap-1 text-xs text-primary hover:underline truncate font-mono"
            >
              {item.url} <ExternalLink className="h-3 w-3" />
            </a>
          )}
          <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
            <Field label="Início" value={fmtDate(item.startedAt)} />
            <Field
              label="Última atualização"
              value={
                item.lastUpdatedAt
                  ? `${fmtDate(item.lastUpdatedAt)} (${daysFromLast}d atrás)`
                  : '—'
              }
            />
            <Field
              label="Próxima atualização"
              value={fmtDate(item.nextUpdateAt)}
              warn={!!updateOverdue}
            />
          </div>
          {item.notes && (
            <p className="mt-2 rounded-md bg-muted/30 p-2 text-xs whitespace-pre-wrap">
              {item.notes}
            </p>
          )}
        </div>
        <div className="flex flex-col gap-1">
          <button
            type="button"
            onClick={onEdit}
            data-dica="Editar"
            className="rounded-md p-1.5 text-muted-foreground hover:bg-accent"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={onDelete}
            data-dica="Excluir"
            className="rounded-md p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, warn }: { label: string; value: string; warn?: boolean }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={warn ? 'font-medium text-amber-700' : 'text-foreground'}>{value}</div>
    </div>
  );
}

// ============================ Form Dialog ============================

function ServiceFormDialog({
  clientId,
  item,
  onClose,
  onSaved,
}: {
  clientId: string;
  item?: ClientServiceItem;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = !!item;
  const [form, setForm] = useState({
    name: item?.name ?? '',
    category: item?.category ?? '',
    status: (item?.status ?? 'ACTIVE') as ClientServiceStatus,
    version: item?.version ?? '',
    url: item?.url ?? '',
    startedAt: item?.startedAt ? item.startedAt.slice(0, 10) : '',
    lastUpdatedAt: item?.lastUpdatedAt ? item.lastUpdatedAt.slice(0, 10) : '',
    nextUpdateAt: item?.nextUpdateAt ? item.nextUpdateAt.slice(0, 10) : '',
    notes: item?.notes ?? '',
  });
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      const body = JSON.stringify({
        name: form.name,
        category: form.category || undefined,
        status: form.status,
        version: form.version || undefined,
        url: form.url || undefined,
        startedAt: form.startedAt || undefined,
        lastUpdatedAt: form.lastUpdatedAt || undefined,
        nextUpdateAt: form.nextUpdateAt || undefined,
        notes: form.notes || undefined,
      });
      return isEdit
        ? apiFetch(`/clients/${clientId}/services/${item!.id}`, {
            method: 'PATCH',
            body,
          })
        : apiFetch(`/clients/${clientId}/services`, { method: 'POST', body });
    },
    onSuccess: onSaved,
    onError: (err) =>
      setError(err instanceof ApiError ? err.body : err instanceof Error ? err.message : 'erro'),
  });

  function handleSubmit(ev: FormEvent) {
    ev.preventDefault();
    setError(null);
    save.mutate();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div
        className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-lg border border-border bg-card p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-base font-medium">
            {isEdit ? `Editar — ${item!.name}` : 'Novo serviço ativo'}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground hover:bg-accent"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <Field2 label="Nome do serviço *">
              <input
                required
                className="input"
                placeholder="Ex.: Nota Fiscal de Serviço"
                value={form.name}
                onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
              />
            </Field2>
            <Field2 label="Categoria">
              <input
                className="input"
                placeholder="NFSe, Integração, PMS, Backup…"
                list="srv-categories"
                value={form.category}
                onChange={(e) => setForm((s) => ({ ...s, category: e.target.value }))}
              />
              <datalist id="srv-categories">
                <option value="NFSe" />
                <option value="NFe" />
                <option value="Integração" />
                <option value="Motor de Reservas" />
                <option value="PMS" />
                <option value="Backup" />
                <option value="Check-in" />
                <option value="Pagamento" />
                <option value="Outros" />
              </datalist>
            </Field2>
            <Field2 label="Status *">
              <select
                required
                className="input"
                value={form.status}
                onChange={(e) =>
                  setForm((s) => ({ ...s, status: e.target.value as ClientServiceStatus }))
                }
              >
                {(Object.keys(STATUS_LABEL) as ClientServiceStatus[]).map((k) => (
                  <option key={k} value={k}>
                    {STATUS_LABEL[k]}
                  </option>
                ))}
              </select>
            </Field2>
            <Field2 label="Versão / Identificação">
              <input
                className="input font-mono"
                placeholder="v2.5 ou prod-homol"
                value={form.version}
                onChange={(e) => setForm((s) => ({ ...s, version: e.target.value }))}
              />
            </Field2>
            <Field2 label="URL / Endereço" full>
              <input
                className="input font-mono text-xs"
                placeholder="https://painel.cliente.com.br"
                value={form.url}
                onChange={(e) => setForm((s) => ({ ...s, url: e.target.value }))}
              />
            </Field2>
            <Field2 label="Início da operação">
              <input
                type="date"
                className="input"
                value={form.startedAt}
                onChange={(e) => setForm((s) => ({ ...s, startedAt: e.target.value }))}
              />
            </Field2>
            <Field2 label="Última atualização realizada">
              <input
                type="date"
                className="input"
                value={form.lastUpdatedAt}
                onChange={(e) => setForm((s) => ({ ...s, lastUpdatedAt: e.target.value }))}
              />
            </Field2>
            <Field2 label="Próxima atualização planejada">
              <input
                type="date"
                className="input"
                value={form.nextUpdateAt}
                onChange={(e) => setForm((s) => ({ ...s, nextUpdateAt: e.target.value }))}
              />
            </Field2>
          </div>
          <Field2 label="Notas internas" full>
            <textarea
              rows={3}
              className="input"
              placeholder="Ex.: cliente quer migrar de servidor em jan/2027 / homologação Sicoob pendente"
              value={form.notes}
              onChange={(e) => setForm((s) => ({ ...s, notes: e.target.value }))}
            />
          </Field2>
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-input px-3 py-2 text-sm hover:bg-accent"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={save.isPending}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {save.isPending ? 'Salvando…' : isEdit ? 'Salvar alterações' : 'Criar serviço'}
            </button>
          </div>
        </form>

        <style>{`
          .input {
            width: 100%;
            border: 1px solid hsl(var(--input));
            background: hsl(var(--background));
            padding: 0.5rem 0.75rem;
            border-radius: 0.375rem;
            font-size: 0.875rem;
          }
          .input:focus { outline: none; box-shadow: 0 0 0 2px hsl(var(--ring) / 0.4); }
        `}</style>
      </div>
    </div>
  );
}

function Field2({
  label,
  full,
  children,
}: {
  label: string;
  full?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className={`space-y-1 block ${full ? 'md:col-span-2' : ''}`}>
      <span className="text-xs font-medium">{label}</span>
      {children}
    </label>
  );
}
